import ResearchArticleCard from "@/components/research/ResearchArticleCard";
import { splitHomeAtHero, splitHomeAtMemberEntry } from "@/components/research/homePlacement";
import { useEffect, useMemo, useRef } from "react";
import Head from "next/head";
import { useSharedRelease } from "@/hooks/useSharedRelease";
import { renderPublicHomeStory } from "./publicHomeStory";
import { initialisePublicHeroMotion } from "./publicHeroMotion";
import ShortLessonsSection from "./ShortLessonsSection";
import ProductWalkthroughSection from "./ProductWalkthroughSection";
import { DURMAH_SOCIAL_PROFILES } from "@/content/videoLessons";

/** The same public story remains available before login and after logout. */
export default function SharedPlatformLanding({ initialReady = false }: { initialReady?: boolean }) {
  const ready = useSharedRelease("trials", initialReady);
  const root = useRef<HTMLElement>(null);
  // Keep the primary study entry ahead of the research card below the hero.
  const html = useMemo(() => renderPublicHomeStory(ready), [ready]);
  const [heroHtml, remainingHtml] = useMemo(() => splitHomeAtHero(html), [html]);
  const [journeyHtml, memberEntryOnwardsHtml] = useMemo(
    () => splitHomeAtMemberEntry(remainingHtml),
    [remainingHtml],
  );
  const organisationSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Durmah",
    url: "https://www.durmah.ai/",
    logo: "https://www.durmah.ai/brand/logos/durmah-logo-stacked.png",
    sameAs: [DURMAH_SOCIAL_PROFILES.instagram, DURMAH_SOCIAL_PROFILES.facebook],
  }).replace(/</g, "\\u003c");
  useEffect(() => {
    if (!root.current) return;
    return initialisePublicHeroMotion(root.current);
  }, [html]);
  return <>
    <Head>
      <title>Learn law. Write law. Speak law. | Durmah</title>
      <meta name="description" content="From your first case to your own legal judgment. Explore Durmah's connected approach to law study, free learning guides and personal study workspace." />
      <link key="route-canonical" rel="canonical" href="https://www.durmah.ai/" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: organisationSchema }} />
      {/* Route-only, fully scoped copy of the approved public design. */}
      {/* eslint-disable-next-line @next/next/no-css-tags */}
      <link rel="stylesheet" href="/home-story/story.css?v=20261021" />
    </Head>
    {/* Generated only from trusted repository content. No user or API text is interpolated. */}
    <main id="durmah-public-home" ref={root}>
      <div dangerouslySetInnerHTML={{ __html: heroHtml }} />
      <div className="px-4"><ResearchArticleCard /></div>
      <div dangerouslySetInnerHTML={{ __html: journeyHtml }} />
      <ProductWalkthroughSection />
      <ShortLessonsSection />
      <div dangerouslySetInnerHTML={{ __html: memberEntryOnwardsHtml }} />
    </main>
  </>;
}
